/**
 * ProfileService.js - Service for managing user profiles via dotstudioPRO API
 * Handles profile creation, management, and authentication operations
 */

import { cacheService } from './CacheService';

// Lazy getter for spotlightService to avoid circular dependency
let _spotlightService = null;
const getSpotlightService = () => {
  if (!_spotlightService) {
    _spotlightService = require('./SpotlightService').default;
  }
  return _spotlightService;
};

/**
 * @typedef {Object} Profile
 * @property {string} _id - Unique profile identifier
 * @property {string} profile_name - Display name for the profile
 * @property {string} profile_thumb - URL for the profile avatar
 */

/**
 * @typedef {Object} ProfilesResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {Profile[]} profiles - Array of profile objects
 * @property {number} remaining - Number of additional profiles that can be created
 */

/**
 * @typedef {Object} ProfileTokenResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {string} token - Profile token for API requests
 */

/**
 * @typedef {Object} ProfileOperationResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {boolean} [profile_created] - Whether profile was created
 * @property {boolean} [profile_updated] - Whether profile was updated
 * @property {boolean} [profile_deleted] - Whether profile was deleted
 * @property {string} [profile_thumb] - New profile avatar URL (for regenerate)
 */

class ProfileService {
  constructor() {
    this.profiles = [];
    this.currentProfile = null;
    this.remainingProfiles = 0;

    // Defer loading from cache to avoid race condition with CacheService initialization
    // Wait for CacheService to be fully initialized before loading from cache
    const loadFromCache = () => {
      // Check if CacheService has loaded critical data from localStorage
      if (cacheService.localStorageAvailable) {
        this.loadCurrentProfileFromCache();
        this.loadProfilesFromCache();
      } else {
        // If CacheService hasn't loaded yet, retry after a short delay

        setTimeout(loadFromCache, 100);
      }
    };

    // Start the loading process
    setTimeout(loadFromCache, 0);
  }

  /**
   * Load current profile from cache
   */
  loadCurrentProfileFromCache() {
    try {
      const cachedProfile = cacheService.getCurrentProfile();
      if (cachedProfile) {
        this.currentProfile = cachedProfile;
      } else {
        // No cached profile — nothing to load, leave currentProfile unset.
      }
    } catch (e) {
      console.warn('ProfileService: Failed to load current profile from cache:', e);
    }
  }

  /**
   * Save current profile to cache
   */
  saveCurrentProfileToCache(profile) {
    try {
      cacheService.saveCurrentProfile(profile);
    } catch (e) {
      console.warn('ProfileService: Failed to save current profile to cache:', e);
    }
  }

  /**
   * Load profiles list from cache
   */
  loadProfilesFromCache() {
    try {
      const cachedProfiles = cacheService.getProfiles();
      if (cachedProfiles && Array.isArray(cachedProfiles)) {
        this.profiles = cachedProfiles;
      }
    } catch (e) {
      console.warn('ProfileService: Failed to load profiles list from cache:', e);
    }
  }

  /**
   * Get all available profiles for the current user
   * @returns {Promise<ProfilesResponse>}
   */
  async getProfiles() {
    try {
      const response = await getSpotlightService().getProfiles();

      if (response.success) {
        this.profiles = response.profiles || [];
        this.remainingProfiles = response.remaining || 0;

        // Cache the profiles list
        cacheService.saveProfiles(this.profiles);
      }

      return response;
    } catch (error) {
      console.error('ProfileService: Error getting profiles:', error);
      throw error;
    }
  }

  /**
   * Create a new profile
   * @param {string} profileName - Name for the new profile
   * @returns {Promise<ProfileOperationResponse>}
   */
  async createProfile(profileName) {
    try {
      const response = await getSpotlightService().createProfile(profileName);

      if (response.success && response.profile_created) {
        // Refresh profiles list
        await this.getProfiles();
      }

      return response;
    } catch (error) {
      console.error('ProfileService: Error creating profile:', error);
      throw error;
    }
  }

  /**
   * Update an existing profile's name
   * @param {string} profileId - Profile ID to update
   * @param {string} newName - New name for the profile
   * @returns {Promise<ProfileOperationResponse>}
   */
  async updateProfile(profileId, newName) {
    try {
      const response = await getSpotlightService().updateProfile(profileId, newName);

      if (response.success && response.profile_updated) {
        // Refresh profiles list
        await this.getProfiles();
      }

      return response;
    } catch (error) {
      console.error('ProfileService: Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Delete a profile
   * @param {string} profileId - Profile ID to delete
   * @returns {Promise<ProfileOperationResponse>}
   */
  async deleteProfile(profileId) {
    try {
      const response = await getSpotlightService().deleteProfile(profileId);

      if (response.success && response.profile_deleted) {
        // Refresh profiles list
        await this.getProfiles();

        // If we deleted the current profile, clear it
        if (this.currentProfile && this.currentProfile._id === profileId) {
          this.currentProfile = null;
          // Clear from cache
          this.saveCurrentProfileToCache(null);
          // Clear profile token from cache
          cacheService.clearProfileToken(profileId);
        }
      }

      return response;
    } catch (error) {
      console.error('ProfileService: Error deleting profile:', error);
      throw error;
    }
  }

  /**
   * Regenerate avatar for a profile
   * @param {string} profileId - Profile ID to regenerate avatar for
   * @returns {Promise<ProfileOperationResponse>}
   */
  async regenerateAvatar(profileId) {
    try {
      const response = await getSpotlightService().regenerateProfileAvatar(profileId);

      if (response.success) {
        // Refresh profiles list to get new avatar URL
        await this.getProfiles();
      }

      return response;
    } catch (error) {
      console.error('ProfileService: Error regenerating avatar:', error);
      throw error;
    }
  }

  /**
   * Get authentication token for a specific profile
   * @param {string} profileId - Profile ID to get token for
   * @returns {Promise<ProfileTokenResponse>}
   */
  async getProfileToken(profileId) {
    try {
      // Use the new cached profile token method
      const profileToken = await getSpotlightService().getProfileTokenWithCache(profileId);

      if (profileToken) {
        // Set the token in SpotlightService for future API calls
        getSpotlightService().setCurrentProfileToken(profileId, profileToken);

        // Find and set the current profile
        this.currentProfile = this.profiles.find(p => p._id === profileId) || null;

        return { success: true, token: profileToken };
      }

      return { success: false, token: null };
    } catch (error) {
      console.error('ProfileService: Error getting profile token:', error);
      throw error;
    }
  }

  /**
   * Set the current active profile and get its token
   * @param {Profile} profile - Profile to set as active
   * @returns {Promise<boolean>} - Whether the profile was set successfully
   */
  async setActiveProfile(profile) {
    try {
      // Clear previous profile data before setting new profile
      if (this.currentProfile && this.currentProfile._id !== profile._id) {
        // Clear user data service state to prevent data contamination
        const { userDataService } = require('./UserDataService');
        userDataService.clearCurrentUser();
        
        // IMPORTANT: Refresh client token when switching profiles
        // The client token is tied to the user+profile combination
        console.log('ProfileService: Refreshing client token for new profile');
        
        // Check if current token exists and is valid before trying to refresh
        const currentToken = getSpotlightService().getClientTokenSync();
        
        if (currentToken) {
          // Try to refresh existing token
          try {
            const refreshResult = await getSpotlightService().refreshClientToken();
            if (!refreshResult) {
              console.warn('ProfileService: Client token refresh returned null - getting fresh token');
              await getSpotlightService().ensureClientToken();
            }
          } catch (tokenError) {
            console.warn('ProfileService: Client token refresh failed, getting fresh token:', tokenError.message);
            try {
              await getSpotlightService().ensureClientToken();
            } catch (freshTokenError) {
              console.error('ProfileService: Failed to get fresh client token:', freshTokenError);
              console.log('ProfileService: Continuing with profile switch despite token issues');
            }
          }
        } else {
          // No current token, get a fresh one directly
          console.log('ProfileService: No current token, getting fresh token directly');
          try {
            await getSpotlightService().ensureClientToken();
          } catch (freshTokenError) {
            console.error('ProfileService: Failed to get fresh client token:', freshTokenError);
            console.log('ProfileService: Continuing with profile switch despite token issues');
          }
        }
      }

      // Use the new cached profile token method
      const profileToken = await getSpotlightService().getProfileTokenWithCache(profile._id);

      if (profileToken) {
        this.currentProfile = profile;

        // Set the token in SpotlightService for future API calls
        getSpotlightService().setCurrentProfileToken(profile._id, profileToken);

        // Save to cache for persistence
        this.saveCurrentProfileToCache(profile);
        return true;
      }

      return false;
    } catch (error) {
      console.error('ProfileService: Error setting active profile:', error);
      return false;
    }
  }

  /**
   * Get the current active profile
   * @returns {Profile|null}
   */
  getCurrentProfile() {
    return this.currentProfile;
  }

  /**
   * Get the current profile token
   * @returns {string|null}
   */
  getCurrentProfileToken() {
    if (this.currentProfile?._id) {
      return cacheService.getProfileToken(this.currentProfile._id);
    }
    return null;
  }

  /**
   * Get all cached profiles
   * @returns {Profile[]}
   */
  getCachedProfiles() {
    return this.profiles;
  }

  /**
   * Get remaining profile slots
   * @returns {number}
   */
  getRemainingProfiles() {
    return this.remainingProfiles;
  }

  /**
   * Clear current profile and token (for logout)
   */
  clearCurrentProfile() {
    this.currentProfile = null;
    // Clear from cache
    this.saveCurrentProfileToCache(null);
    // Clear profile token from cache
    if (this.currentProfile?._id) {
      cacheService.clearProfileToken(this.currentProfile._id);
    }
  }

  /**
   * Clear all profile data (for logout)
   */
  clearAllData() {
    this.profiles = [];
    this.currentProfile = null;
    this.remainingProfiles = 0;
    // Clear all profile cache
    cacheService.clearProfileCache();
    // Clear all profile tokens
    cacheService.clearAllProfileTokens();
  }
}

// Create and export singleton instance
const profileService = new ProfileService();
export default profileService;
